import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Paper, CircularProgress, Grid } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { roleContext } from '../../context/RoleContext';
import { UserRole } from '../../types/role';
import {
  getBookingsByShopId,
  getBookingsByBarberId,
} from '../../middleware/reservation_service/getRequests';
import { Booking } from '../../types/booking';

interface ServiceData {
  name: string;
  value: number;
  revenue: number;
}

interface BarberRevenueData {
  name: string;
  value: number;
}

const COLORS = [
  '#ce8b32',
  '#8b5a1f',
  '#d4a24e',
  '#a67c38',
  '#e8b968',
  '#6b4f2a',
  '#b8924a',
  '#9d7c3f',
  '#f0c878',
  '#7d5c2e',
];

const RevenueSection: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [serviceData, setServiceData] = useState<ServiceData[]>([]);
  const [barberRevenueData, setBarberRevenueData] = useState<
    BarberRevenueData[]
  >([]);

  const role = useContext(roleContext);
  const partnerItem = localStorage.getItem('reg_partner_id');
  const partnerId = BigInt(
    partnerItem ? partnerItem.replace(/[^0-9]/g, '') : 0
  );
  const shopItem = localStorage.getItem('reg_shop_id');
  const shopId = BigInt(shopItem ? shopItem.replace(/[^0-9]/g, '') : 0);

  useEffect(() => {
    loadRevenueData();
  }, [role, partnerId, shopId]);

  const loadRevenueData = async () => {
    setLoading(true);
    const setServices = (
      booking: Booking,
      serviceMap: Map<string, ServiceData>
    ) => {
      const serviceName = booking.service_name || 'Unknown Service';
      if (serviceName === 'Unknown Service') return;
      const existing = serviceMap.get(serviceName);
      if (existing) {
        existing.value += 1;
        existing.revenue += booking.price || 0;
      } else {
        serviceMap.set(serviceName, {
          name: serviceName,
          value: 1,
          revenue: booking.price || 0,
        });
      }
    };

    try {
      let response;
      if (role === UserRole.BARBER) {
        response = await getBookingsByBarberId(partnerId);
      } else {
        response = await getBookingsByShopId(shopId);
      }

      const bookings: Booking[] = Array.isArray(response.data)
        ? response.data
        : [];

      const shopBarberBookings: Booking[] = bookings.filter(
        (booking) => Number(booking.barber_id) === Number(partnerId)
      );

      // Calculate total revenue
      if (role === UserRole.SHOP_BARBER) {
        const total = shopBarberBookings.reduce((sum, booking) => {
          return sum + (booking.price || 0);
        }, 0);
        setTotalRevenue(total);
      } else {
        const total = bookings.reduce((sum, booking) => {
          return sum + (booking.price || 0);
        }, 0);
        setTotalRevenue(total);
      }

      // Group by service
      const serviceMap = new Map<string, ServiceData>();
      if (role === UserRole.SHOP_BARBER) {
        shopBarberBookings.forEach((booking) => {
          setServices(booking, serviceMap);
        });
      } else {
        bookings.forEach((booking) => {
          setServices(booking, serviceMap);
        });
      }
      setServiceData(Array.from(serviceMap.values()));

      // For shop owners only, calculate revenue per barber
      if (role === UserRole.SHOP_OWNER) {
        const barberMap = new Map<string, number>();
        bookings.forEach((booking) => {
          const barberName = booking.barber_name || 'Unknown Barber';
          if (barberName === 'Unknown Barber') return;
          const currentRevenue = barberMap.get(barberName) || 0;
          barberMap.set(barberName, currentRevenue + (booking.price || 0));
        });

        const barberData: BarberRevenueData[] = Array.from(
          barberMap.entries()
        ).map(([name, value]) => ({
          name,
          value,
        }));
        setBarberRevenueData(barberData);
      }
    } catch (error) {
      console.error('Failed to load revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Revenue
      </Typography>

      <Box mb={3}>
        <Typography variant="subtitle2" color="text.secondary">
          Total Revenue Since Joining
        </Typography>
        <Typography variant="h4" fontWeight="bold" color="primary">
          £{totalRevenue.toFixed(2)}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={role === UserRole.SHOP_OWNER ? 6 : 12}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Services Breakdown
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={serviceData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}`}
                outerRadius={70}
                fill="#8884d8"
                dataKey="value"
                style={{ fontSize: 14 }}
              >
                {serviceData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length && payload[0]) {
                    const data = payload[0].payload;
                    return (
                      <Box
                        sx={{
                          bgcolor: 'white',
                          p: 1,
                          border: '1px solid #ccc',
                        }}
                      >
                        <Typography variant="body2">
                          {data.name}: {data.value} bookings (£
                          {data.revenue.toFixed(2)})
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              <Legend wrapperStyle={{ fontSize: 14 }} />
            </PieChart>
          </ResponsiveContainer>
        </Grid>

        {role === UserRole.SHOP_OWNER && barberRevenueData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Revenue per Shop Barber
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={barberRevenueData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: £${entry.value.toFixed(2)}`}
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="value"
                  style={{ fontSize: 14 }}
                >
                  {barberRevenueData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length && payload[0]) {
                      const data = payload[0];
                      const value =
                        typeof data.value === 'number' ? data.value : 0;
                      return (
                        <Box
                          sx={{
                            bgcolor: 'white',
                            p: 1,
                            border: '1px solid #ccc',
                          }}
                        >
                          <Typography variant="body2">
                            {data.name}: £{value.toFixed(2)}
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 14 }} />
              </PieChart>
            </ResponsiveContainer>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default RevenueSection;
